import React, { useState, useRef, useEffect } from "react";
import {
  Modal,
  Box,
  Button,
  Fade,
  Backdrop,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ReactSignatureCanvas from "react-signature-canvas";
import Compressor from "compressorjs";
import "./React-siganture.scss";
import { useTranslation } from "react-i18next";

const ReactSignature = ({
  clearSignatureData,
  openSignature,
  getSignature,
  closeSignature,
  defaultSignatureImage,
  clearSign,
}) => {
  const { t } = useTranslation();
  const signatureRef = useRef(null);
  const [signatureFile, setSignatureFile] = useState(null);
  const [signature, setSignature] = useState(null);
  const [isSignatureCaptured, setIsSignatureCaptured] = useState(false);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  useEffect(() => {
    if (clearSignatureData) reSign();
  }, [clearSignatureData]);

  useEffect(() => {
    if (defaultSignatureImage) {
      setSignature(defaultSignatureImage?.[0]?.url || null);
      setIsSignatureCaptured(true);
    }
  }, [defaultSignatureImage]);

  const reSign = () => {
    signatureRef.current?.clear();
    setSignature(null);
    setIsSignatureCaptured(false);
    getSignature(null);
    clearSign();
  };

  const handleConfirmation = () => {
    const imageSrc = signatureRef.current?.toDataURL();
    setIsSignatureCaptured(true);
    if (!defaultSignatureImage) {
      getSignature(signatureFile);
      setSignature(imageSrc);
    }
    closeSignature(false);
  };

  const handleSign = () => {
    const imageSrc = signatureRef.current?.toDataURL();
    setIsSignatureCaptured(true);
    formatIntoPng(imageSrc).then(setSignatureFile).catch(console.error);
  };

  const handleClose = () => {
    if (!signature) {
      setIsSignatureCaptured(false);
    }
    closeSignature(false);
  };

  const formatIntoPng = (dataURL) =>
    new Promise((resolve, reject) => {
      const file = dataURLtoFile(dataURL, "customer-image");
      new Compressor(file, {
        quality: 0.8,
        success: resolve,
        error: reject,
      });
    });

  const dataURLtoFile = (dataurl, filename) => {
    const [mimePart, dataPart] = dataurl.split(",");
    const mime = mimePart.match(/:(.*?);/)[1];
    const bstr = atob(dataPart);
    const u8arr = new Uint8Array(bstr.length);
    for (let i = 0; i < bstr.length; i++) u8arr[i] = bstr.charCodeAt(i);
    return new File([u8arr], filename, { type: mime });
  };

  return (
    <Box>
      <Modal
        onClose={handleClose}
        open={openSignature}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
      >
        <Fade in={openSignature}>
          <Box
            sx={{
              width: { xs: "91.795vw", sm: "60%", md: "37%" },
              height: { xs: "111.282vw", sm: "auto", md: "auto" },
              alignItems: "center",
              backgroundColor: "rgba(0, 0, 0, 0.05)",
              textAlign: "center",
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.paper",
              borderRadius: { xs: "24px", md: "30px" },
              padding: { xs: "3.5% 0", md: "2% 0" },
            }}
          >
            {isMobile && (
              <Box
                sx={{
                  height: { xs: "8.899vh" },
                  width: { xs: "91.795vw" },
                }}
              ></Box>
            )}
            <Box
              className="signature-function"
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                height: "100%",
              }}
            >
              <Box
                sx={{
                  width: { xs: "66.154vw", sm: "90%" },
                  margin: "0 auto",
                  height: { xs: "18.444vh", sm: "320px" },
                  borderRadius: { xs: "15px", sm: "20px" },
                  border: `1px dashed ${signature ? "#037A00" : "black"}`,
                }}
              >
                {!signature ? (
                  <ReactSignatureCanvas
                    ref={signatureRef}
                    penColor="black"
                    onEnd={handleSign}
                    canvasProps={{ className: "reactcanvas-signature" }}
                  />
                ) : (
                  <Box className="reactcanvas-signature">
                    <img
                      src={signature}
                      alt="captured-image"
                      height="100%"
                      width="100%"
                    />
                  </Box>
                )}
              </Box>
              <Box sx={{ marginTop: "1%", position: "relative" }}>
                {isSignatureCaptured && (
                  <Button
                    onClick={reSign}
                    size="small"
                    sx={{
                      color: "#4A4AFF",
                      fontSize: { xs: "3.08vw", sm: "1.68vw", md: "0.972vw" },
                      fontWeight: 500,
                      textTransform: "none",
                      position: "absolute",
                      left: "40%",
                    }}
                    variant="text"
                  >
                    {t("pages.booking_form.clear_signature")}
                  </Button>
                )}
                <Typography
                  sx={{
                    fontSize: { xs: "3.08vw", sm: "1.68vw", md: "0.972vw" },
                    fontWeight: 500,
                    color: "#6B6B6B",
                    margin: "8% auto 0 auto",
                    width: "50%",
                  }}
                >
                  {t(
                    "pages.booking_form.align_your_signature_within_the_frame_and_tap_confirm"
                  )}
                </Typography>
              </Box>
              <Box
                sx={{
                  position: isMobile ? "fixed" : "",
                  width: "90%",
                  bottom: isMobile ? "10%" : "auto",
                  left: isMobile ? "50%" : "",
                  transform: isMobile ? "translateX(-50%)" : "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  margin: "12% auto 0 auto",
                }}
              >
                <Button
                  onClick={handleClose}
                  sx={{
                    width: "48%",
                    fontWeight: 600,
                    fontSize: { xs: "3.59vw", sm: "1.92vw", md: "1.12vw" },
                  }}
                  variant="outlined"
                >
                  {t("pages.booking_form.close")}
                </Button>
                <Button
                  fullWidth
                  disabled={!isSignatureCaptured}
                  onClick={handleConfirmation}
                  sx={{
                    width: "48%",
                    fontWeight: 600,
                    fontSize: { xs: "3.59vw", sm: "1.92vw", md: "1.12vw" },
                  }}
                  variant="contained"
                >
                  {t("pages.booking_form.confirm")}
                </Button>
              </Box>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default ReactSignature;
